import { useRef, useState, useEffect } from "react";
import { toast } from "sonner";
import { money } from "../lib/format";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "./ui/dialog";
import {
  DownloadSimple, Printer, FileText, TrendDown, CheckCircle, Storefront,
  Scales, Images, Plus, X, EnvelopeSimple, Copy, Spinner, ArrowsClockwise,
  Lightning, Sparkle, ChartBar,
} from "@phosphor-icons/react";
import { BarChart, Bar, Cell, XAxis, YAxis, ResponsiveContainer, LabelList } from "recharts";
import { pdfUrl, fileUrl, letterPdfUrl, uploadPhotos, deletePhoto, generateLetter, saveLetter } from "../lib/api";

const goldBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-gradient-to-r from-[#C5A059] to-[#E5C17B] hover:brightness-110 text-black font-bold text-xs uppercase tracking-widest rounded-sm transition-all shadow-md shadow-[#C5A059]/20 active:scale-[0.98] disabled:opacity-60";
const ghostBtn =
  "inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-black/40 hover:bg-white/10 text-[#C5A059] hover:text-[#E5C17B] font-semibold text-xs uppercase tracking-wider rounded-sm transition-all border border-[#C5A059]/40 active:scale-[0.98] disabled:opacity-60";

const Empty = () => (
  <div
    className="h-full min-h-[60vh] flex flex-col items-center justify-center text-center p-12 border border-dashed border-white/10 bg-[#0F0F11] rounded-xl"
    data-testid="report-empty-state"
  >
    <div className="h-16 w-16 rounded-full bg-[#C5A059]/10 border border-[#C5A059]/20 flex items-center justify-center mb-5">
      <FileText size={30} className="text-[#C5A059]" />
    </div>
    <h3 className="font-display text-xl font-medium text-white">Your appraisal dossier will appear here</h3>
    <p className="text-sm text-gray-400 mt-2 max-w-sm leading-relaxed">
      Upload an insurance estimate PDF or complete the vehicle details, then run the appraisal to
      generate a printable diminished-value report.
    </p>
  </div>
);

const Loading = () => (
  <div className="bg-[#0F0F11] border border-white/10 rounded-xl p-8 sm:p-10 space-y-6" data-testid="report-loading">
    <div className="h-28 bg-[#C5A059]/10 rounded-lg animate-pulse" />
    <div className="grid grid-cols-2 gap-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="h-12 bg-black/40 rounded-md animate-pulse" />
      ))}
    </div>
    <div className="h-24 bg-black/40 rounded-lg animate-pulse" />
    <p className="text-center text-sm text-[#C5A059] font-medium flex items-center justify-center gap-2">
      <Sparkle size={16} className="animate-spin" /> Analyzing retail market data &amp; computing diminished value…
    </p>
  </div>
);

const Row = ({ label, value, strong, accent }) => (
  <div className="flex items-center justify-between py-2 border-b border-white/5">
    <span className="text-sm text-gray-400">{label}</span>
    <span className={`font-mono-num text-sm ${strong ? "font-bold" : "font-semibold"} ${accent ? "text-[#E5C17B]" : "text-gray-200"}`}>
      {value}
    </span>
  </div>
);

const SectionTitle = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2.5 mb-3">
    <div className="p-1.5 bg-[#C5A059]/10 text-[#C5A059] rounded-sm border border-[#C5A059]/20">
      <Icon size={15} weight="bold" />
    </div>
    <h4 className="text-sm font-display font-bold text-white tracking-wide">{children}</h4>
  </div>
);

const card = "bg-[#0F0F11] rounded-xl border border-white/10 shadow-2xl p-6 sm:p-7";

export const ReportPreview = ({ appraisal, loading, onRefresh }) => {
  const fileRef = useRef(null);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  const [letterOpen, setLetterOpen] = useState(false);
  const [letterLoading, setLetterLoading] = useState(false);
  const [letterText, setLetterText] = useState("");
  const [savingLetter, setSavingLetter] = useState(false);

  const letterSrc = appraisal?.demand_letter?.text || "";
  useEffect(() => {
    setLetterText(letterSrc);
  }, [letterSrc]);

  if (loading) return <Loading />;
  if (!appraisal) return <Empty />;

  const v = appraisal.vehicle || {};
  const r = appraisal.results || {};
  const comps = r.comparable_listings || [];
  const sr = r.state_rules || {};
  const photos = appraisal.photos || [];
  const letter = appraisal.demand_letter;
  const vehicleName = `${v.year || ""} ${v.make || ""} ${v.model || ""} ${v.trim || ""}`.trim();

  const preVal = Number(r.pre_accident_value ?? r.pre_accident_wholesale_value ?? 0);
  const postVal = Number(r.post_repair_value ?? r.post_repair_wholesale_value ?? 0);
  const chartData = [
    { name: "Pre-Accident", v: preVal, key: "pre" },
    { name: "Post-Repair", v: postVal, key: "post" },
  ];

  const handlePrint = () => window.print();

  const handlePhotos = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    setUploadingPhotos(true);
    try {
      const updated = await uploadPhotos(appraisal.id, files);
      onRefresh?.(updated);
      toast.success("Photos added to report.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Photo upload failed.");
    } finally {
      setUploadingPhotos(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removePhoto = async (photoId) => {
    try {
      const updated = await deletePhoto(appraisal.id, photoId);
      onRefresh?.(updated);
    } catch {
      toast.error("Could not remove photo.");
    }
  };

  const makeLetter = async () => {
    setLetterOpen(true);
    if (letter?.text) return;
    setLetterLoading(true);
    try {
      const res = await generateLetter(appraisal.id);
      setLetterText(res.text || "");
      onRefresh?.({ ...appraisal, demand_letter: res });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Letter generation failed.");
      setLetterOpen(false);
    } finally {
      setLetterLoading(false);
    }
  };

  const regenerateLetter = async () => {
    if (letterText && letterText !== letterSrc &&
        !window.confirm("Regenerating will replace your current edits with a new AI draft. Continue?")) {
      return;
    }
    setLetterLoading(true);
    try {
      const res = await generateLetter(appraisal.id);
      setLetterText(res.text || "");
      onRefresh?.({ ...appraisal, demand_letter: res });
      toast.success("Letter regenerated.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Regeneration failed.");
    } finally {
      setLetterLoading(false);
    }
  };

  const persistLetter = async () => {
    const res = await saveLetter(appraisal.id, letterText);
    onRefresh?.({ ...appraisal, demand_letter: res });
    return res;
  };

  const saveLetterEdits = async () => {
    setSavingLetter(true);
    try {
      await persistLetter();
      toast.success("Letter saved.");
    } catch {
      toast.error("Could not save letter.");
    } finally {
      setSavingLetter(false);
    }
  };

  const downloadLetterPdf = async () => {
    setSavingLetter(true);
    try {
      await persistLetter();
      window.open(letterPdfUrl(appraisal.id), "_blank");
    } catch {
      toast.error("Could not prepare PDF.");
    } finally {
      setSavingLetter(false);
    }
  };

  const copyLetter = () => {
    navigator.clipboard.writeText(letterText || "");
    toast.success("Letter copied to clipboard.");
  };

  return (
    <div className="animate-fade-up space-y-6" data-testid="report-preview">
      {/* Action bar */}
      <div className="flex items-center justify-between print:hidden gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-gray-500">
          <FileText size={16} /> Appraisal Dossier
        </div>
        <div className="flex items-center gap-2">
          <button onClick={makeLetter} data-testid="demand-letter-btn" className={ghostBtn}>
            <EnvelopeSimple size={16} /> Demand Letter
          </button>
          <button onClick={handlePrint} data-testid="print-report-btn" className={ghostBtn}>
            <Printer size={16} /> Print
          </button>
          <a href={pdfUrl(appraisal.id)} data-testid="export-pdf-btn" className={goldBtn}>
            <DownloadSimple size={16} /> Export PDF
          </a>
        </div>
      </div>

      {/* Headline */}
      <div className="bg-[#0F0F11] border border-[#C5A059]/40 rounded-xl p-6 sm:p-8 shadow-2xl relative overflow-hidden">
        <div className="absolute right-0 top-0 w-96 h-96 bg-[#C5A059]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C5A059]/60 to-transparent" />
        <div className="relative z-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#C5A059]/10 border border-[#C5A059]/30 text-[#E5C17B] rounded-sm text-[10px] uppercase tracking-widest font-semibold mb-3">
            <Lightning size={12} weight="fill" className="text-[#C5A059]" /> Retail Market Value Loss
          </div>
          <div className="flex items-baseline gap-3">
            <h2 className="font-display text-5xl sm:text-6xl font-bold text-white tracking-tight" data-testid="market-loss-value">
              {money(r.market_loss)}
            </h2>
            <span className="text-xs uppercase tracking-widest text-[#C5A059] font-medium">USD</span>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-1 mt-4 text-sm text-gray-400">
            <span>Recommended claim: <strong className="text-[#E5C17B]">{money(r.recommended_claim)}</strong></span>
            <span>Value lost: <strong className="text-[#E5C17B]">{r.market_loss_pct ?? "—"}%</strong></span>
            {r.confidence && (
              <span className="inline-flex items-center gap-1 text-[#10B981]">
                <CheckCircle size={14} weight="fill" /> {r.confidence} confidence
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Vehicle */}
      <div className={card}>
        <SectionTitle icon={FileText}>Vehicle</SectionTitle>
        <div className="font-display text-xl font-medium text-white" data-testid="report-vehicle-name">
          {vehicleName || "—"}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/10 mt-4 rounded-lg overflow-hidden">
          {[
            ["Mileage", `${Number(v.mileage || 0).toLocaleString()} mi`],
            ["Type", v.vehicle_type || "—"],
            ["Location", v.location || "—"],
            ["VIN", v.vin || "—"],
          ].map(([k, val]) => (
            <div key={k} className="bg-[#0F0F11] p-3">
              <div className="text-[10px] uppercase tracking-wide text-gray-500">{k}</div>
              <div className="text-sm font-medium text-gray-200 truncate mt-0.5" title={val}>{val}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Market valuation */}
      <div className={card}>
        <SectionTitle icon={TrendDown}>Market Valuation · Primary Method · Retail</SectionTitle>
        <div className="bg-black/40 rounded-lg p-4 border border-white/10">
          <Row label="Pre-accident retail value" value={money(r.pre_accident_value ?? r.pre_accident_wholesale_value)} />
          <Row label="Post-repair retail value" value={money(r.post_repair_value ?? r.post_repair_wholesale_value)} />
          <div className="flex items-center justify-between pt-2.5">
            <span className="text-sm text-gray-300 font-semibold">Diminished value (market loss)</span>
            <span className="font-mono-num text-lg font-bold text-[#E5C17B]">{money(r.market_loss)}</span>
          </div>
        </div>
        {/* Pre vs Post bar chart */}
        <div className="mt-4 bg-black/40 rounded-lg p-4 border border-white/10" data-testid="value-chart">
          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-gray-400 mb-2">
            <ChartBar size={13} className="text-[#C5A059]" /> Value impact
          </div>
          <ResponsiveContainer width="100%" height={170}>
            <BarChart data={chartData} margin={{ top: 20, right: 10, left: 10, bottom: 0 }}>
              <XAxis dataKey="name" tick={{ fill: "#9ca3af", fontSize: 11 }} axisLine={{ stroke: "#3f3f46" }} tickLine={false} />
              <YAxis hide domain={[0, "dataMax"]} />
              <Bar dataKey="v" radius={[4, 4, 0, 0]} barSize={70}>
                {chartData.map((d) => (
                  <Cell key={d.key} fill={d.key === "pre" ? "#C5A059" : "#6b7280"} />
                ))}
                <LabelList dataKey="v" position="top" formatter={(val) => money(val)}
                  style={{ fill: "#E5E7EB", fontSize: 11, fontWeight: 700 }} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="flex items-center justify-center gap-2 text-xs text-gray-400 -mt-1">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-[#C5A059]" /> Pre-accident
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-500 ml-2" /> Post-repair
            <span className="ml-2">Drop of <strong className="text-[#E5C17B]">{money(r.market_loss)}</strong></span>
          </div>
        </div>
        {r.reasoning && (
          <p className="text-sm text-gray-400 leading-relaxed mt-3">
            <span className="font-semibold text-gray-200">Analysis: </span>{r.reasoning}
          </p>
        )}
        {r.vehicle_type_factor && (
          <p className="text-sm text-gray-400 leading-relaxed mt-2">
            <span className="font-semibold text-gray-200">Vehicle type: </span>{r.vehicle_type_factor}
          </p>
        )}
        {r.locale_factor && (
          <p className="text-sm text-gray-400 leading-relaxed mt-2">
            <span className="font-semibold text-gray-200">Locale: </span>{r.locale_factor}
          </p>
        )}
      </div>

      {/* Comparable listings */}
      {comps.length > 0 && (
        <div className={card} data-testid="comps-section">
          <SectionTitle icon={Storefront}>Comparable Market Listings · Evidence</SectionTitle>
          <div className="border border-white/10 rounded-lg overflow-x-auto">
            <table className="w-full border-collapse text-sm min-w-[520px]">
              <thead>
                <tr className="bg-black/60 text-[#E5C17B]">
                  {["Vehicle", "Mileage", "Location", "Source", "Price"].map((h) => (
                    <th key={h} className="text-left font-semibold p-2.5 text-[10px] uppercase tracking-wider border-b border-white/10">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comps.map((c, i) => (
                  <tr key={i} className="border-b border-white/5">
                    <td className="p-2.5 text-gray-200">
                      {`${c.year || ""} ${c.make || ""} ${c.model || ""} ${c.trim || ""}`.trim()}
                    </td>
                    <td className="p-2.5 font-mono-num text-gray-400">{Number(c.mileage || 0).toLocaleString()} mi</td>
                    <td className="p-2.5 text-gray-400">{c.location || "—"}</td>
                    <td className="p-2.5 text-gray-400">{c.source || "—"}</td>
                    <td className="p-2.5 font-mono-num font-bold text-[#E5C17B]">{money(c.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 italic mt-2">
            Representative for-sale comparables of similar vehicles supporting the retail market value behind this claim.
          </p>
        </div>
      )}

      {/* State rules */}
      {sr.state_name && (
        <div className={card} data-testid="state-rules-section">
          <SectionTitle icon={Scales}>State DV Rules · {sr.state_name}</SectionTitle>
          <div className="border-l-2 border-[#C5A059] bg-black/40 p-4 space-y-2 rounded-r-lg">
            <p className="text-sm text-gray-300"><span className="font-semibold text-white">Claim deadline: </span>{sr.deadline_note}</p>
            <p className="text-sm text-gray-300"><span className="font-semibold text-white">Your rights: </span>{sr.first_party_note}</p>
            <p className="text-xs text-gray-500 italic">{sr.disclaimer}</p>
          </div>
        </div>
      )}

      {/* Photos */}
      <div className={card} data-testid="photos-section">
        <div className="flex items-center justify-between mb-3">
          <SectionTitle icon={Images}>Damage &amp; Repair Photos</SectionTitle>
          <div className="print:hidden -mt-3">
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden"
              onChange={handlePhotos} data-testid="photo-input" />
            <button onClick={() => fileRef.current?.click()} disabled={uploadingPhotos}
              data-testid="add-photo-btn" className={ghostBtn}>
              {uploadingPhotos ? <Spinner size={15} className="animate-spin" /> : <Plus size={15} />} Add Photos
            </button>
          </div>
        </div>
        {photos.length === 0 ? (
          <div className="border border-dashed border-white/10 bg-black/40 p-6 text-center text-sm text-gray-500 rounded-lg">
            No photos yet. Add damage &amp; repair photos to include them in the exported PDF.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {photos.map((p) => (
              <div key={p.id} className="relative group border border-white/10 aspect-[4/3] overflow-hidden bg-black rounded-lg"
                data-testid={`photo-${p.id}`}>
                <img src={fileUrl(p.storage_path)} alt={p.original_filename} className="w-full h-full object-cover" />
                <button onClick={() => removePhoto(p.id)} data-testid={`delete-photo-${p.id}`}
                  className="print:hidden absolute top-1.5 right-1.5 h-7 w-7 bg-black/70 hover:bg-black rounded-sm flex items-center justify-center text-white hover:text-rose-400 opacity-0 group-hover:opacity-100 transition-opacity">
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Demand letter dialog */}
      <Dialog open={letterOpen} onOpenChange={setLetterOpen}>
        <DialogContent className="max-w-2xl bg-[#0F0F11] border-white/10 text-gray-200" data-testid="letter-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2 text-white">
              <EnvelopeSimple size={20} className="text-[#C5A059]" /> Insurance Demand Letter
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Review, edit, regenerate, then save or download your demand letter.
            </DialogDescription>
          </DialogHeader>
          {letterLoading ? (
            <div className="py-16 flex flex-col items-center text-gray-400">
              <Spinner size={30} className="animate-spin mb-3 text-[#C5A059]" />
              <span className="text-sm">Drafting your demand letter…</span>
            </div>
          ) : (
            <>
              <Textarea
                value={letterText}
                onChange={(e) => setLetterText(e.target.value)}
                data-testid="letter-text"
                spellCheck={true}
                className="font-mono-num text-[13px] leading-relaxed text-gray-200 bg-[#0A0A0B] border border-white/10 p-4 h-[45vh] rounded-md focus-visible:ring-1 focus-visible:ring-[#C5A059]"
              />
              <div className="flex flex-wrap justify-between gap-2 pt-2">
                <button onClick={regenerateLetter} disabled={savingLetter} data-testid="regenerate-letter-btn" className={ghostBtn}>
                  <ArrowsClockwise size={16} /> Regenerate
                </button>
                <div className="flex gap-2">
                  <button onClick={copyLetter} data-testid="copy-letter-btn" className={ghostBtn}>
                    <Copy size={16} /> Copy
                  </button>
                  <button onClick={saveLetterEdits} disabled={savingLetter} data-testid="save-letter-btn" className={ghostBtn}>
                    {savingLetter ? <Spinner size={16} className="animate-spin" /> : <FileText size={16} />} Save
                  </button>
                  <button onClick={downloadLetterPdf} disabled={savingLetter} data-testid="letter-pdf-btn" className={goldBtn}>
                    <DownloadSimple size={16} /> Download PDF
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
